import { Search, SentimentDissatisfied } from "@mui/icons-material";
import {
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import { Typography } from "@mui/material";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { config } from "../App";
import Footer from "./Footer";
import Header from "./Header";
import "./Products.css";
import ProductCard from "./ProductCard";
import Cart, { generateCartItemsFrom } from "./Cart";

// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 *
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

const Products = () => {
  const { enqueueSnackbar } = useSnackbar();
  let [loading, setLoading] = useState(false);
  let [productsData, setProductsData] = useState([]);
  let [searchErr, setSearchErr] = useState(false);
  let [cartItems, setCartItems] = useState([]);
  const [originalProducts, setOriginalProducts] = useState([]);

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Fetch products data and store it
  /**
   * Make API call to get the products list and store it to display the products
   *
   * @returns { Array.<Product> }
   *      Array of objects with complete data on all available products
   *
   * API endpoint - "GET /products"
   *
   * Example for successful response from backend:
   * HTTP 200
   * [
   *      {
   *          "name": "iPhone XR",
   *          "category": "Phones",
   *          "cost": 100,
   *          "rating": 4,
   *          "image": "https://i.imgur.com/lulqWzW.jpg",
   *          "_id": "v4sLtEcMpzabRyfx"
   *      },
   *      {
   *          "name": "Basketball",
   *          "category": "Sports",
   *          "cost": 100,
   *          "rating": 5,
   *          "image": "https://i.imgur.com/lulqWzW.jpg",
   *          "_id": "upLK9JbQ4rMhTwt4"
   *      }
   * ]
   *
   * Example for failed response from backend:
   * HTTP 500
   * {
   *      "success": false,
   *      "message": "Something went wrong. Check the backend console for more details"
   * }
   */

  const performAPICall = async () => {
    try {
      setLoading(true);
      let response = await axios.get(`${config.endpoint}/products`);
      setLoading(false);
      setProductsData(response.data);
      setOriginalProducts(response.data);
      return response.data;
    } catch (err) {
      setLoading(false);
      console.log(err.response);
    }
  };

  const handleAddToCart = async (item, value, action) => {
    if (localStorage.getItem("token") === null) {
      enqueueSnackbar("Login To Add Product to Cart", {
        variant: "warning",
        autoHideDuration: 1500,
      });
      return;
    }
    if (action === undefined) {
      if (isItemInCart(item)) {
        enqueueSnackbar("Item Already In Cart", {
          variant: "warning",
          autoHideDuration: 1500,
        });
        return;
      }
    }
    try {
      let obj;
      if (action) {
        if (action === "add") {
          obj = {
            productId: item._id,
            qty: value + 1,
          };
        } else if (action === "remove") {
          obj = {
            productId: item._id,
            qty: value - 1,
          };
        }
      } else {
        obj = {
          productId: item._id,
          qty: 1,
        };
      }
      let response = await axios.post(`${config.endpoint}/cart`, obj, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setCartItems(response.data);
    } catch (err) {
      console.log(err);
    }
  };

  const isItemInCart = (item) => {
    let bool = false;
    if (cartItems) {
      cartItems.forEach((cartItem) => {
        if (cartItem.productId === item._id) {
          bool = true;
        }
      });
    }
    return bool;
  };

  // Fetch Cart
  const fetchCart = async (token) => {
    if (!token) return;
    try {
      let res = await axios.get(`${config.endpoint}/cart`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return res.data;
    } catch (err) {
      if (err.response && err.response.status === 400) {
        enqueueSnackbar(err.response.data.message, {
          variant: "error",
          autoHideDuration: 1500,
        });
      } else {
        enqueueSnackbar(
          "Could not fetch cart details. Check that the backend is running, reachable and returns valid JSON.",
          {
            variant: "error",
            autoHideDuration: 1500,
          }
        );
      }
      return null;
    }
  };

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Implement search logic
  /**
   * Definition for search handler
   * This is the function that is called on adding new search keys
   *
   * @param {string} text
   *    Text user types in the search bar. To filter the displayed products based on this text.
   *
   * @returns { Array.<Product> }
   *      Array of objects with complete data on filtered set of products
   *
   * API endpoint - "GET /products/search?value=<search-query>"
   *
   */
  const performSearch = async (text) => {
    try {
      let response = await axios.get(
        `${config.endpoint}/products/search?value=${text}`
      );

      if (response && response.data && response.data.length > 0) {
        setProductsData(response.data);
        setSearchErr(false);
      } else {
        setSearchErr(true);
      }
    } catch (err) {
      console.log(err.response);
      setSearchErr(true);
    }
  };

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Optimise API calls with debounce search implementation
  /**
   * Definition for debounce handler
   * With debounce, this is the function to be called whenever the user types text in the searchbar field
   *
   * @param {{ target: { value: string } }} event
   *    JS event object emitted from the search input field
   *
   * @param {NodeJS.Timeout} debounceTimeout
   *    Timer id set for the previous debounce call
   *
   */
  const debounceSearch = (performSearch, debounceTimeout) => {
    let timer;
    return function (event) {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        await performSearch(event.target.value);
      }, debounceTimeout);
    };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(async () => {
    performAPICall();
    let token = localStorage.getItem("token");
    let cartItem = await fetchCart(token);
    setCartItems(cartItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Header hasHiddenAuthButtons={true}>
        <Box className="search-desktop">
          <TextField
            onChange={debounceSearch(performSearch, 500)}
            size="small"
            placeholder="Search for items/categories"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search color="primary" />
                </InputAdornment>
              ),
            }}
            style={{ minWidth: 300 }}
          />
        </Box>
      </Header>

      {/* Search view for mobiles */}
      <TextField
        className="search-mobile"
        size="small"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Search color="primary" />
            </InputAdornment>
          ),
        }}
        placeholder="Search for items/categories"
        name="search"
        onChange={debounceSearch(performSearch, 500)}
      />
      <Grid container>
        <Grid
          container
          item
          md={localStorage.getItem("token") !== null ? 9 : 12}
        >
          <Grid container item>
            <Grid item className="product-grid">
              <Box className="hero">
                <p className="hero-heading">
                  India’s{" "}
                  <span className="hero-highlight">FASTEST DELIVERY</span> to
                  your door step
                </p>
              </Box>
            </Grid>
          </Grid>
          <Grid container item style={{ minHeight: "60vh", width: "100%" }}>
            {loading ? (
              <Grid
                item
                container
                lg={12}
                direction={"column"}
                justifyContent="center"
                alignItems={"center"}
              >
                <Grid item>
                  <CircularProgress />
                </Grid>
                <Grid item marginTop={1}>
                  <Typography>Loading Products...</Typography>
                </Grid>
              </Grid>
            ) : searchErr === true ? (
              <Grid
                item
                container
                lg={12}
                direction={"column"}
                justifyContent="center"
                alignItems={"center"}
              >
                <Grid item>
                  <SentimentDissatisfied />
                </Grid>
                <Grid item marginTop={1}>
                  <Typography>No products found</Typography>
                </Grid>
              </Grid>
            ) : (
              <>
                <Grid
                  container
                  spacing={1}
                  style={{
                    paddingLeft: 10,
                    paddingRight: 10,
                    paddingTop: 30,
                    paddingBottom: 30,
                  }}
                >
                  {productsData &&
                    productsData.map((product) => (
                      <Grid item xs={6} md={3} key={product._id}>
                        <ProductCard
                          product={product}
                          handleAddToCart={handleAddToCart}
                        />
                      </Grid>
                    ))}
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
        <Grid
          container
          item
          display={localStorage.getItem("token") !== null ? "flex" : "none"}
          md={localStorage.getItem("token") !== null ? 3 : 0}
          xs={localStorage.getItem("token") !== null ? 12 : 0}
          style={{ backgroundColor: "#E9F5E1", padding: 8 }}
        >
          <Grid
            item
            container
            direction={"column"}
            justifyContent={"center"}
            style={{ height: "fit-content" }}
          >
            <Grid item container direction="column">
              <Cart
                items={generateCartItemsFrom(cartItems, originalProducts)}
                handleQuantity={handleAddToCart}
                products={productsData}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Footer />
    </div>
  );
};

export default Products;
